import { Button, cn } from '@shared/components/shadcn';
import ChevronRight from '@shared/icons/ChevronRight';
import type { IconProps } from '@shared/icons/types';
import Link from 'next/link';
import type { NavigationPodProps } from './types';

const CHEVRON_STYLES = 'shrink-0 w-5 h-5 ml-auto';

const renderDefaultLabel = (variant?: string, label?: string, secondaryLabel?: string) => {
  if (variant === 'contact' && secondaryLabel) {
    return (
      <div className="flex min-w-0 flex-col">
        <span className="paragraph-explanatory-strong underline">{label}</span>
        <span className="paragraph-s whitespace-pre-line">{secondaryLabel}</span>
      </div>
    );
  }
  return <span className="paragraph-explanatory-strong">{label}</span>;
};

/**
 * Renders a NavigationPod component, which is a stylized link used for navigation tiles.
 *
 * Supports three visual variants:
 * - 'default': Displays an icon and a label, with optional stacked layout.
 * - 'action': Displays an icon, label, and either a button or a right-pointing chevron.
 * - 'provider': Displays custom children content only, without any icon or label, e.g. a 3rd party provider logo.
 *
 * NOTE: only 'default' and 'action' explicit variants exist in the design system, but it was easier to create a 3rd for provider pods.
 */
function NavigationPod(props: NavigationPodProps) {
  const {
    href,
    className,
    linkClass,
    variant = 'default',
    icon,
    iconDirection,
    label,
    secondaryLabel,
    hierarchy = 'default',
    buttonText,
    children,
    renderLabel = renderDefaultLabel,
    'data-testid': dataTestId,
    'data-track-name': dataTrackName,
    ...rest
  } = props;
  const variantStyles = { wrapper: '', iconWrapper: '', icon: '', button: '', chevron: '', children: '' };

  const styles = {
    variants: {
      default: {
        ...variantStyles,
        wrapper: cn(
          'flex items-center justify-center gap-1 w-full py-4 px-3 paragraph-strong text-foreground bg-container-base border border-border rounded-lg text-nowrap truncate hover:bg-container',
          iconDirection === 'stacked' && 'flex-col',
          className
        ),
        icon: 'w-8 h-8 text-accent-green-foreground',
      },
      action: {
        ...variantStyles,
        wrapper: cn(
          'flex items-center justify-between gap-4 w-full py-3 px-4 paragraph-strong text-foreground bg-container rounded-lg text-nowrap truncate',
          hierarchy === 'high' ? 'text-foreground-inverse bg-container-active hover:bg-container-active/90' : 'hover:bg-container/90',
          className
        ),
        iconWrapper: cn('flex flex-col items-center justify-center shrink-0 w-10 h-10 bg-accent-green rounded-full'),
        icon: 'w-8 h-8 text-foreground dark:text-foreground-inverse',
        button: 'flex items-center justify-center gap-2 h-8 py-2 px-4 text-foreground bg-button-background-inverse rounded-lg hover:bg-background-inverse-hover',
        chevron: CHEVRON_STYLES,
      },
      provider: {
        ...variantStyles,
        wrapper: cn('flex items-center justify-center gap-1 w-full h-14 px-4 py-2 bg-container-base border border-border rounded-lg hover:bg-container', className),
        children: cn('navigationPodChildren flex justify-center align-center shrink-0 w-19 rounded-sm'),
      },
      contact: {
        ...variantStyles,
        wrapper: cn(
          'group flex h-full min-h-full w-full items-center justify-between gap-4 py-3 px-4',
          'paragraph-strong text-foreground rounded-lg',
          'bg-button-background-inverse hover:bg-button-background-inverse-hover',
          'dark:hover:bg-container-secondary',
          className
        ),
        iconWrapper: cn(
          'flex h-10 w-10 shrink-0 items-center justify-center rounded-full',
          'bg-contact-pod-icon-background border border-contact-pod-icon-border group-hover:border-transparent'
        ),
        icon: 'w-5 h-5 text-contact-pod-icon-foreground',
        chevron: CHEVRON_STYLES,
      },
    },
  };

  const classes = styles.variants[variant];
  const showIcon = variant !== 'provider' && !!icon;
  const showLabel = variant !== 'provider' && label;
  const showButton = variant === 'action' && buttonText;
  const showChevron = (variant === 'action' || variant === 'contact') && !buttonText;

  const Chevron = ChevronRight as React.ComponentType<IconProps>;
  const IconComponent = icon as React.ComponentType<IconProps>;
  const Icon = icon && variant !== 'provider' && (
    <div className={classes.iconWrapper}>
      <IconComponent className={classes.icon} />
    </div>
  );

  return (
    <Link className={cn(variant === 'contact' && 'flex h-full min-h-full', linkClass)} href={href ?? ''}>
      <div className={classes.wrapper} data-testid={dataTestId} data-track-name={dataTrackName} {...rest}>
        {showIcon && Icon}
        {showLabel && renderLabel(variant, label, secondaryLabel)}
        {showButton && (
          <Button variant="secondary" size="s" className={classes.button}>
            {buttonText}
          </Button>
        )}
        {showChevron && <Chevron size="regular" className={classes.chevron} />}
        {variant === 'provider' && <div className={classes.children}>{children}</div>}
      </div>
    </Link>
  );
}

export type { NavigationPodProps, NavigationPodRenderLabel } from './types';
export { NavigationPod };
